import { throttle } from 'throttle-debounce';
import {cInfo, subscribe} from '../humans/helper';
import $ from "cash-dom";

/**
 * Component to add scroll-dependent classes to the body
 * @package KAA
 */
export default class ScrollListener {
    /**
     * Singleton instance of the component
     */
    static #instance: ScrollListener;

    /**
     * Cash body reference
     */
    protected $body;

    /**
     * UI references
     */
    protected ui = {
        footer: ".js-footer",
    };

    /**
     * Local state to track if anchor scrolling is active
     */
    private isAnchorScrolling: boolean = false;

    protected lastScrollPosition: number = 0;
    protected isScrollingUp: boolean = false;
    protected hasScrolled: boolean = false; // Track if scrolled at least once
    protected throttledScrollHandler: () => void;
    protected throttledResizeHandler: () => void;
    protected viewportHeight: number = 0; // Store window height

    protected constructor() {
        this.$body = $("body");
        this.throttledScrollHandler = throttle(50, this.handleScroll.bind(this));
        this.throttledResizeHandler = throttle(200, this.calculateViewportHeight.bind(this));
        this.calculateViewportHeight();
        this.registerEvents();
        this.initFooterObserver();

        // Check initial scroll position (page might be loaded already scrolled)
        if (window.scrollY > 0) {
            this.hasScrolled = true;
            this.$body.addClass('has-scrolled');
        }

        // Subscribe to anchor scrolling start and end events
        subscribe('anchorScroll.start', this.handleAnchorScrolling.bind(this, true));
        subscribe('anchorScroll.end', this.handleAnchorScrolling.bind(this, false));
    }

    /**
     * Returns the singleton instance of the component
     */
    public static getInstance(): ScrollListener {
        if (!ScrollListener.#instance) {
            ScrollListener.#instance = new ScrollListener();
        }

        return ScrollListener.#instance;
    }

    /**
     * Register required events
     */
    protected registerEvents(): void {
        window.addEventListener('scroll', this.throttledScrollHandler, { passive: true });
        window.addEventListener('resize', this.throttledResizeHandler, { passive: true });
    }

    /**
     * Calculate viewport height - called once on init and on window resize
     */
    protected calculateViewportHeight(): void {
        this.viewportHeight = window.innerHeight;
        cInfo('Viewport height calculated:', this.viewportHeight);
    }

    /**
     * Handle scroll event
     */
    protected handleScroll(): void {
        if (this.isAnchorScrolling) return;

        const currentScrollPosition = window.scrollY;
        const isScrolled = currentScrollPosition > 0;
        const isScrolled100vh = currentScrollPosition > this.viewportHeight;
        const isScrolledSome = currentScrollPosition > this.viewportHeight * 0.2;

        // Check if this is the first time scrolling
        if (!this.hasScrolled && isScrolled) {
            this.hasScrolled = true;
            this.$body.addClass('has-scrolled');
            cInfo('First scroll detected');
        }

        // Determine scroll direction
        this.isScrollingUp = currentScrollPosition < this.lastScrollPosition;

        // Update body classes using cash-dom
        const classMap = {
            'is-scrolled': isScrolled,
            'is-scrolled-100vh': isScrolled100vh,
            'is-scrolled-some': isScrolledSome,
            'is-scrolled-up': this.isScrollingUp,
        };

        // Update classes using cash-dom
        Object.entries(classMap).forEach(([className, shouldAdd]) => {
            if (shouldAdd) {
                this.$body.addClass(className);
            } else {
                this.$body.removeClass(className);
            }
        });

        // Update the last scroll position
        this.lastScrollPosition = currentScrollPosition;
    }

    /**
     * Initialize Intersection Observer for .footer
     */
    protected initFooterObserver(): void {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const parent = entry.target.parentElement;
                if (entry.isIntersecting) {
                    $(parent)?.addClass("has-footer-in-view");
                } else {
                    $(parent)?.removeClass("has-footer-in-view");
                }
            });
        }, {
            root: null, // Use viewport as root
            threshold: 0, // Trigger when first px of the element is visible
            rootMargin: "0px 0px 0px 0px"
        });

        // Observe all .js-footer elements
        $(this.ui.footer).each((_, el) => {
            observer.observe(el);
        });
    }

    /**
     * Handle anchor scrolling events
     * @param isScrolling Whether anchor scrolling is active
     */
    protected handleAnchorScrolling(isScrolling: boolean): void {
        this.isAnchorScrolling = isScrolling;
        // cInfo('isAnchorScrolling', isScrolling);
    }
}